import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Textarea } from '../components/ui/textarea';
import { 
  Dialog, 
  DialogContent, 
  DialogHeader, 
  DialogTitle, 
  DialogDescription,
  DialogFooter 
} from '../components/ui/dialog';
import { 
  User, MapPin, Briefcase, Mail, Phone, Download, 
  CheckCircle, XCircle, Loader2, FileText, Clock,
  Building2, AlertCircle, ShieldCheck
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function DossierPage() {
  const { token } = useParams();
  const [dossier, setDossier] = useState(null);
  const [branding, setBranding] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState('cover-letter');
  
  // Decision
  const [decisionModalOpen, setDecisionModalOpen] = useState(false);
  const [pendingDecision, setPendingDecision] = useState(null);
  const [notes, setNotes] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [decisionSubmitted, setDecisionSubmitted] = useState(false);

  useEffect(() => {
    const loadDossier = async () => {
      try {
        const res = await fetch(`${API_URL}/api/public/cv/${token}`);
        if (res.ok) {
          const data = await res.json();
          setDossier(data.dossier);
          setBranding(data.branding);
          if (data.dossier.decision) {
            setDecisionSubmitted(true);
          }
        } else {
          const err = await res.json();
          setError(err.detail || 'Failed to load dossier');
        }
      } catch (e) {
        console.error('Error loading dossier:', e);
        setError('Failed to load dossier. Please check the link and try again.');
      }
      setLoading(false);
    };

    loadDossier();
  }, [token]);

  const handleDecisionClick = (decision) => {
    setPendingDecision(decision);
    setDecisionModalOpen(true);
  };

  const submitDecision = async () => {
    setSubmitting(true);
    try {
      const formData = new FormData();
      formData.append('decision', pendingDecision);
      if (notes) formData.append('notes', notes);

      const res = await fetch(`${API_URL}/api/public/cv/${token}/decision`, {
        method: 'POST',
        body: formData
      });

      if (res.ok) {
        const data = await res.json();
        toast.success(data.message);
        setDecisionSubmitted(true);
        setDecisionModalOpen(false);
        setDossier(prev => ({ ...prev, decision: pendingDecision }));
      } else {
        const err = await res.json();
        toast.error(err.detail || 'Failed to submit decision');
      }
    } catch (e) {
      console.error('Error:', e);
      toast.error('Error submitting decision');
    }
    setSubmitting(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-10 h-10 animate-spin text-slate-400 mx-auto" />
          <p className="text-slate-500 mt-4">Loading dossier...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center p-4">
        <Card className="max-w-md w-full">
          <CardContent className="pt-8 pb-8 text-center">
            <AlertCircle className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h1 className="text-xl font-semibold text-slate-800 mb-2">Unable to Load Dossier</h1>
            <p className="text-slate-600">{error}</p>
          </CardContent>
        </Card>
      </div>
    );
  }

  const candidate = dossier?.candidate_snapshot || {};
  const requirement = dossier?.requirement_snapshot || {};

  // ---------------------------------------------------------------------------
  // Redaction-aware page chrome.
  // This page reads the same endpoint and accepts the same share token as
  // /cv/:token. Without these checks a client sent a redacted link could simply
  // change /cv/ to /dossier/ in the URL and read the candidate's real name,
  // employer, email and phone - defeating the redaction entirely. Where nothing
  // is being hidden, everything renders exactly as it did before.
  // ---------------------------------------------------------------------------
  const redaction = dossier?.redaction || {};
  const hideName = redaction.hide_candidate_name === true;
  const hideEmployer = redaction.hide_employers === true;
  const identityProtected = hideName || hideEmployer;
  const displayName = hideName
    ? (dossier?.dossier_ref || 'Confidential Candidate')
    : (candidate.full_name || dossier?.dossier_ref || 'Confidential Candidate');
  const displayEmployer = hideEmployer
    ? (candidate.current_industry || candidate.current_company_descriptor || 'Current employer withheld')
    : candidate.current_company;

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header with Company Logo */}
      <header className="bg-white border-b">
        <div className="max-w-4xl mx-auto px-6 py-6 max-md:px-4 max-md:py-4">
          <div className="flex items-center justify-between max-md:flex-wrap max-md:gap-3">
            <div className="flex items-center gap-3">
              {branding?.company_logo ? (
                <img 
                  src={branding.company_logo} 
                  alt={branding.company_name} 
                  className="h-10 object-contain"
                />
              ) : (
                <div className="w-10 h-10 bg-slate-800 rounded-lg flex items-center justify-center">
                  <Building2 className="w-6 h-6 text-white" />
                </div>
              )}
              <div>
                <p className="font-medium text-slate-800">{branding?.company_name || 'Recruitment Agency'}</p>
                <p className="text-xs text-slate-500">Candidate Dossier</p>
              </div>
            </div>
            {requirement.client_name && (
              <Badge variant="outline" className="text-sm">
                For: {requirement.client_name}
              </Badge>
            )}
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-6 py-12 max-md:px-4 max-md:py-8">
        {/* Candidate Header */}
        <div className="mb-12 max-md:mb-8">
          <h1 className="text-4xl font-bold text-slate-900 mb-3 max-md:text-2xl max-md:break-words" data-testid="candidate-name">
            {displayName}
          </h1>
          
          <div className="flex flex-wrap items-center gap-4 text-slate-600 mb-4 max-md:gap-x-3 max-md:gap-y-2 max-md:text-sm">
            {candidate.current_designation && (
              <div className="flex items-center gap-1.5">
                <Briefcase className="w-4 h-4" />
                <span>{candidate.current_designation}</span>
              </div>
            )}
            {displayEmployer && (
              <div className="flex items-center gap-1.5">
                <Building2 className="w-4 h-4" />
                <span>{displayEmployer}</span>
              </div>
            )}
            {candidate.location && (
              <div className="flex items-center gap-1.5">
                <MapPin className="w-4 h-4" />
                <span>{candidate.location}</span>
              </div>
            )}
            {candidate.total_experience_years && (
              <div className="flex items-center gap-1.5">
                <Clock className="w-4 h-4" />
                <span>{candidate.total_experience_years} years experience</span>
              </div>
            )}
          </div>

          {identityProtected ? (
            <div className="flex items-center gap-2 text-sm text-emerald-700 bg-emerald-50 border border-emerald-200 rounded-lg px-3 py-2 w-fit" data-testid="identity-protected-note">
              <ShieldCheck className="w-4 h-4 flex-shrink-0" />
              <span>Identifying and contact details are withheld at this stage and shared on mutual interest.</span>
            </div>
          ) : (
            <div className="flex flex-wrap items-center gap-4 text-sm text-slate-500">
              {candidate.email && (
                <div className="flex items-center gap-1.5">
                  <Mail className="w-4 h-4" />
                  <span>{candidate.email}</span>
                </div>
              )}
              {candidate.phone && (
                <div className="flex items-center gap-1.5">
                  <Phone className="w-4 h-4" />
                  <span>{candidate.phone}</span>
                </div>
              )}
            </div>
          )}
        </div>

        {/* Position Applied For */}
        {requirement.position_title && (
          <div className="mb-8 p-4 bg-slate-100 rounded-lg">
            <p className="text-sm text-slate-500 mb-1">Applying for</p>
            <p className="text-lg font-semibold text-slate-800">{requirement.position_title}</p>
            {requirement.location && (
              <p className="text-sm text-slate-500 mt-1">{requirement.location}</p>
            )}
          </div>
        )}

        {/* Skills Section */}
        {candidate.skills?.length > 0 && (
          <div className="mb-10">
            <h2 className="text-lg font-semibold text-slate-800 mb-4">Skills & Expertise</h2>
            <div className="flex flex-wrap gap-2">
              {candidate.skills.map((skill, idx) => (
                <Badge 
                  key={idx} 
                  variant="secondary" 
                  className="px-3 py-1.5 text-sm bg-slate-100 text-slate-700 hover:bg-slate-200"
                >
                  {skill}
                </Badge>
              ))}
            </div>
          </div>
        )}

        {/* Tabs: Cover Letter & Resume */}
        <Tabs value={activeTab} onValueChange={setActiveTab} className="mb-12">
          <TabsList className="mb-6 max-md:w-full max-md:h-auto">
            <TabsTrigger value="cover-letter" className="px-6 max-md:flex-1 max-md:px-2 max-md:min-h-[44px]">
              <FileText className="w-4 h-4 mr-2" />
              Cover Letter
            </TabsTrigger>
            <TabsTrigger value="resume" className="px-6 max-md:flex-1 max-md:px-2 max-md:min-h-[44px]">
              <Download className="w-4 h-4 mr-2" />
              Resume
            </TabsTrigger>
          </TabsList>

          <TabsContent value="cover-letter">
            <Card>
              <CardContent className="pt-8 pb-8 px-8 max-md:px-4 max-md:py-6">
                <div className="prose prose-slate max-w-none">
                  <p className="text-slate-700 leading-relaxed whitespace-pre-wrap text-base max-md:break-words">
                    {dossier?.cover_letter || candidate.summary || 'No cover letter available.'}
                  </p>
                </div>
              </CardContent>
            </Card>
          </TabsContent>

          <TabsContent value="resume">
            <Card>
              <CardContent className="pt-8 pb-8 px-8 max-md:px-4 max-md:py-6">
                {candidate.resume_storage_path ? (
                  <div className="text-center">
                    <FileText className="w-16 h-16 text-slate-300 mx-auto mb-4" />
                    <p className="text-slate-600 mb-4">
                      {identityProtected
                        ? (dossier?.dossier_ref ? `${dossier.dossier_ref} - Confidential CV` : 'Confidential CV')
                        : (candidate.resume_filename || 'Resume')}
                    </p>
                    <a
                      href={`${API_URL}/api/public/cv/${token}/resume`}
                      target="_blank"
                      rel="noreferrer"
                      className="inline-flex items-center gap-2 bg-slate-800 text-white px-6 py-3 rounded-lg hover:bg-slate-700 transition-colors max-md:w-full max-md:justify-center max-md:min-h-[48px]"
                      data-testid="download-resume-btn"
                    >
                      <Download className="w-5 h-5" />
                      Download Resume
                    </a>
                  </div>
                ) : (
                  <div className="text-center py-8 text-slate-500">
                    <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                    <p>No resume attached to this profile</p>
                  </div>
                )}

                {/* Summary Section */}
                {candidate.summary && (
                  <div className="mt-8 pt-8 border-t">
                    <h3 className="font-semibold text-slate-800 mb-3">Professional Summary</h3>
                    <p className="text-slate-600 leading-relaxed">{candidate.summary}</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        </Tabs>

        {/* Decision Section */}
        {!decisionSubmitted && !dossier?.decision ? (
          <div className="bg-white rounded-xl border p-8 text-center max-md:p-5">
            <h2 className="text-xl font-semibold text-slate-800 mb-2">Your Decision</h2>
            <p className="text-slate-600 mb-6">
              Would you like to proceed with this candidate?
            </p>
            <div className="flex justify-center gap-4 max-md:flex-col max-md:gap-3">
              <Button
                size="lg"
                variant="outline"
                className="border-red-200 text-red-600 hover:bg-red-50 hover:border-red-300 px-8 max-md:w-full max-md:min-h-[48px]"
                onClick={() => handleDecisionClick('rejected')}
                data-testid="reject-btn"
              >
                <XCircle className="w-5 h-5 mr-2" />
                Reject
              </Button>
              <Button
                size="lg"
                className="bg-green-600 hover:bg-green-700 px-8 max-md:w-full max-md:min-h-[48px]"
                onClick={() => handleDecisionClick('approved')}
                data-testid="approve-btn"
              >
                <CheckCircle className="w-5 h-5 mr-2" />
                Approve
              </Button>
            </div>
          </div>
        ) : (
          <div className={`rounded-xl p-8 text-center ${
            dossier?.decision === 'approved' 
              ? 'bg-green-50 border border-green-200' 
              : 'bg-red-50 border border-red-200'
          }`}>
            {dossier?.decision === 'approved' ? (
              <>
                <CheckCircle className="w-12 h-12 text-green-600 mx-auto mb-3" />
                <h2 className="text-xl font-semibold text-green-800">Candidate Approved</h2>
                <p className="text-green-700 mt-1">Thank you for your decision. The recruiter has been notified.</p>
              </>
            ) : (
              <>
                <XCircle className="w-12 h-12 text-red-500 mx-auto mb-3" />
                <h2 className="text-xl font-semibold text-red-800">Candidate Rejected</h2>
                <p className="text-red-700 mt-1">Thank you for your decision. The recruiter has been notified.</p>
              </>
            )}
          </div>
        )}

        {/* Footer */}
        <footer className="mt-16 pt-8 border-t text-center text-sm text-slate-500 max-md:mt-10 max-md:pb-[env(safe-area-inset-bottom)]">
          <p>This dossier was shared by {branding?.company_name || 'the recruitment team'}.</p>
          {dossier?.expires_at && (
            <p className="mt-1">
              Link expires: {new Date(dossier.expires_at).toLocaleDateString('en-US', {
                year: 'numeric',
                month: 'long',
                day: 'numeric'
              })}
            </p>
          )}
        </footer>
      </main>

      {/* Decision Confirmation Modal */}
      <Dialog open={decisionModalOpen} onOpenChange={setDecisionModalOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className={pendingDecision === 'approved' ? 'text-green-700' : 'text-red-700'}>
              {pendingDecision === 'approved' ? 'Approve Candidate' : 'Reject Candidate'}
            </DialogTitle>
            <DialogDescription>
              {pendingDecision === 'approved' 
                ? `You are about to approve ${displayName} for the ${requirement.position_title || 'position'}.`
                : `You are about to reject ${displayName} for the ${requirement.position_title || 'position'}.`
              }
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <label className="text-sm font-medium text-slate-700">
              Notes (optional)
            </label>
            <Textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              placeholder="Add any feedback or comments for the recruiter..."
              rows={3}
              className="mt-2"
              data-testid="decision-notes"
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDecisionModalOpen(false)}>
              Cancel
            </Button>
            <Button 
              onClick={submitDecision}
              disabled={submitting}
              className={pendingDecision === 'approved' ? 'bg-green-600 hover:bg-green-700' : 'bg-red-600 hover:bg-red-700'}
              data-testid="confirm-decision-btn"
            >
              {submitting && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              Confirm {pendingDecision === 'approved' ? 'Approval' : 'Rejection'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
